import React, { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';

import { HTMLHeading } from '@varicent/components';
import { GetRowIdParams, GridOptions } from 'ag-grid-community';

import buildActivityFilesPanelColumnDef from 'app/components/AdvancedGrid/GridHelpers/ActivityFilesPanel/ActivityFilesPanelColumnDef';
import GridV2 from 'app/components/AdvancedGrid/GridV2';

import { CELL_HEIGHT } from 'app/constants/DataTrayConstants';

import { useData } from 'app/contexts/dataProvider';
import { useScope } from 'app/contexts/scopeProvider';

import { BLOCK_SIZE } from 'app/global/variables';

import { FileTypeEnum, GetFileList, GetFileListVariables } from 'app/graphql/generated/graphqlApolloTypes';
import { handleError } from 'app/graphql/handleError';
import { useGetFileListLazy } from 'app/graphql/queries/getFileList';

import { DataPanelViews, FileStatus } from 'app/models/index';

import block from 'utils/bem-css-modules';
import { formatMessage } from 'utils/messages/utils';

import style from './UnpublishedTable.module.pcss';

const b = block(style);

interface UnpublishedTableProps {
  shouldRefetch: boolean;
  setShouldRefetch: (value: boolean) => void;
  setSelectedDataView: Dispatch<SetStateAction<DataPanelViews>>;
  onDeleteClick: () => void;
}

const UnpublishedTable: React.FC<UnpublishedTableProps> = ({
  shouldRefetch,
  setShouldRefetch,
  setSelectedDataView,
  onDeleteClick
}: UnpublishedTableProps) => {
  const { selectedPlanningCycle } = useScope();
  const { setSelectedTable, shouldRefetchActivityTable, setShouldRefetchActivityTable, selectedTablesTab } = useData();

  const containerRef = useRef(null);
  const [blockSize, setBlockSize] = useState<number>(BLOCK_SIZE);

  useEffect(() => {
    if (containerRef?.current?.offsetHeight) {
      setBlockSize(Math.round((containerRef.current.offsetHeight / CELL_HEIGHT) * 2));
    }
  }, [containerRef]);

  const [getUnpublishedFiles, { fetchMore }] = useGetFileListLazy({
    variables: {
      planningCycleId: selectedPlanningCycle?.id,
      tableInput: {
        startRow: 1,
        endRow: blockSize,
        uploadFilter: {
          fileTypes: [FileTypeEnum.Activity],
          statuses: [
            FileStatus.PENDING,
            FileStatus.IN_PROGRESS,
            FileStatus.FAILED,
            FileStatus.PENDING_VALIDATION,
            FileStatus.CHECKING_PROGRESS,
            FileStatus.VALIDATING
          ]
        }
      }
    },
    fetchPolicy: 'no-cache',
    onError({ graphQLErrors, networkError }) {
      handleError(graphQLErrors, networkError);
    }
  });

  useEffect(() => {
    if (shouldRefetchActivityTable) {
      getUnpublishedFiles();
      setShouldRefetchActivityTable(false);
    }
  }, [shouldRefetchActivityTable]);

  useEffect(() => {
    if (shouldRefetch) {
      getUnpublishedFiles();
      setShouldRefetch(false);
    }
  }, [shouldRefetch]);

  const onViewTableClicked = (data) => {
    setSelectedDataView(DataPanelViews.TABLE_DETAIL);
    setSelectedTable({ ...data, planningCycleId: selectedPlanningCycle.id });
  };

  const onDeleteTableClicked = (data) => {
    setSelectedTable({ ...data, planningCycleId: selectedPlanningCycle.id });
    onDeleteClick();
  };

  const fileListPanelGridOptions = useMemo(
    (): GridOptions => ({
      defaultColDef: {
        resizable: true
      },
      columnDefs: buildActivityFilesPanelColumnDef({
        onViewItemClicked: onViewTableClicked,
        onDeleteItemClicked: onDeleteTableClicked
      }),
      rowSelection: {
        mode: 'singleRow',
        checkboxes: false,
        enableClickSelection: true
      },
      rowModelType: 'serverSide',
      cacheBlockSize: blockSize,
      cacheOverflowSize: 2,
      maxBlocksInCache: 10,
      onCellClicked: (event) => {
        if (!event.data) return;
        const isMenuColumn = event.colDef.field === 'menu';
        const isInProgress = event.data.status === FileStatus.IN_PROGRESS;
        if (isMenuColumn || isInProgress) {
          return;
        }
        setSelectedTable(event.data);
        setSelectedDataView(DataPanelViews.TABLE_DETAIL);
      },
      getRowId: (params: GetRowIdParams) => params.data.tableId,
      serverSideDatasource: {
        getRows: (params) => {
          (async () => {
            try {
              if (!params) return;
              const { startRow, endRow } = params.request;
              const fetchMoreFiles = await fetchMore<GetFileList, GetFileListVariables>({
                variables: {
                  planningCycleId: selectedPlanningCycle?.id,
                  tableInput: {
                    startRow: startRow + 1,
                    endRow,
                    uploadFilter: {
                      fileTypes: [FileTypeEnum.Activity],
                      statuses: [
                        FileStatus.PENDING,
                        FileStatus.IN_PROGRESS,
                        FileStatus.FAILED,
                        FileStatus.CHECKING_PROGRESS
                      ]
                    }
                  }
                }
              });
              params.success({
                rowData: fetchMoreFiles.data.getPlanningCycleSpec.tables.tableList,
                rowCount: fetchMoreFiles.data.getPlanningCycleSpec.tables.totalNumOfTables
              });
            } catch {
              params.success({ rowData: [], rowCount: 0 });
            }
          })();
        }
      }
    }),
    [selectedPlanningCycle, shouldRefetch, shouldRefetchActivityTable, selectedTablesTab]
  );

  return (
    <div className={b()}>
      <div className={b('headingContainer')}>
        <HTMLHeading tagLevel={'h5'} bold text={formatMessage('UNPUBLISHED')} />
      </div>
      <div className={b('tablesGrid')}>
        <GridV2 {...fileListPanelGridOptions} data-testid="tables-unpublished-grid" />
      </div>
    </div>
  );
};

export default UnpublishedTable;
